import React, { useState, useRef, useEffect } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  styled,
} from '@mui/material'

// Create a button that looks like text but shows it's clickable on hover
const TextButton = styled(Button)(({}) => ({
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  fontFamily: 'inherit',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  color: 'inherit',
  textTransform: 'none',
  display: 'inline',
  minWidth: 'auto',
  cursor: 'text',
  '&:hover': {
    background: 'none',
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}))

interface MovingButtonModalProps {
  open: boolean
  onClose: () => void
  title?: string
  content?: string
  onAccept: () => void
}

export const MovingButtonModal: React.FC<MovingButtonModalProps> = ({
  open,
  onClose,
  title = 'Terms and Conditions',
  content,
  onAccept,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0 })
  const [imposterButtonPosition, setImposterButtonPosition] = useState({
    x: 0,
    y: 0,
  })
  const [clickCount, setClickCount] = useState(0)
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 })
  const [showImposter, setShowImposter] = useState(false)
  const [dialogContent, setDialogContent] = useState(content)

  // Update window size on mount and resize
  useEffect(() => {
    const updateWindowSize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      })
    }

    // Initial size
    updateWindowSize()

    // Add event listener for resize
    window.addEventListener('resize', updateWindowSize)

    // Cleanup
    return () => window.removeEventListener('resize', updateWindowSize)
  }, [])

  // Reset content when modal reopens
  useEffect(() => {
    if (open) {
      setDialogContent(content)
    }
  }, [open, content])

  // Button text options based on click count
  const buttonTexts = [
    'Accept',
    'Confirm Acceptance',
    'Last Acceptance',
    'Submit',
    'Confirm Submittance',
    'Did you mean to submit?',
    'One more time',
    'Click "Here" to close',
  ]

  const messages = [
    'Please accept the terms',
    "We didn't quite get if you accepted the terms.",
    "Last time we'll make you accept the terms",
    "Please submit once. Don't question submissions",
    "And confirm you've submitted by submitting",
    'Did you really mean it?',
    'One more time',
    '"Here"',
  ]

  const handleImposterClick = () => {
    // Reset click count
    setClickCount(0)
    // Reset positions
    setButtonPosition({ x: 0, y: 0 })
    setImposterButtonPosition({ x: 0, y: 0 })
    setShowImposter(false)
    // Change content
    setDialogContent(
      "Oops, you clicked the wrong button. Let's try that again."
    )
  }

  const handleHereClick = () => {
    onAccept()
    // Reset for next time
    setClickCount(0)
    setButtonPosition({ x: 0, y: 0 })
    setImposterButtonPosition({ x: 0, y: 0 })
    setShowImposter(false)
  }

  // Handle button click - move the button and update text
  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    void event

    if (!buttonRef.current) return

    // Get button dimensions
    const buttonRect = buttonRef.current.getBoundingClientRect()
    const buttonWidth = buttonRect.width
    const buttonHeight = buttonRect.height

    // Calculate a random direction to move
    const randomAngle = Math.random() * 2 * Math.PI

    // If the stars align, move it far away, otherwise, a modest amount
    const moveDistance = Math.random() === Math.random() ? 15000 : 500
    let moveX = Math.cos(randomAngle) * moveDistance
    let moveY = Math.sin(randomAngle) * moveDistance

    // Get current modal position
    const dialogRect = buttonRef.current
      .closest('.MuiDialog-paper')
      ?.getBoundingClientRect()
    if (!dialogRect) return

    const buttonAbsoluteX = dialogRect.left + dialogRect.width / 2 // Center of dialog + current transform
    const buttonAbsoluteY = dialogRect.top + dialogRect.height / 2

    // Calculate max allowed movement to stay within screen bounds (with 20px padding)
    const padding = 20
    const maxX = windowSize.width - buttonAbsoluteX - buttonWidth / 2 - padding
    const minX = -buttonAbsoluteX + buttonWidth / 2 + padding
    const maxY =
      windowSize.height - buttonAbsoluteY - buttonHeight / 2 - padding
    const minY = -buttonAbsoluteY + buttonHeight / 2 + padding

    // Check if button would be partially off screen before clamping
    const wouldBePartiallyHidden =
      moveX > maxX - buttonWidth / 2 ||
      moveX < minX + buttonWidth / 2 ||
      moveY > maxY - buttonHeight / 2 ||
      moveY < minY + buttonHeight / 2

    // Clamp the movement to stay within screen bounds
    moveX = Math.min(Math.max(moveX, minX), maxX)
    moveY = Math.min(Math.max(moveY, minY), maxY)

    setButtonPosition({ x: moveX, y: moveY })

    // If button would be partially hidden, show imposter button
    if (wouldBePartiallyHidden) {
      // Position imposter button near the real one but slightly more visible
      const imposterAngle = randomAngle + Math.PI / 4 // Offset angle
      let imposterX = Math.cos(imposterAngle) * (moveDistance * 0.8)
      let imposterY = Math.sin(imposterAngle) * (moveDistance * 0.8)

      // Ensure imposter is also within bounds but at edge
      imposterX = Math.min(Math.max(imposterX, minX), maxX)
      imposterY = Math.min(Math.max(imposterY, minY), maxY)

      setImposterButtonPosition({ x: imposterX, y: imposterY })
      setShowImposter(true)
    } else {
      setShowImposter(false)
    }

    // Increment click count and wrap around if needed
    setClickCount((prevCount) => (prevCount + 1) % buttonTexts.length)

    // If we're on the last text option and they click again, actually accept
    if (clickCount === buttonTexts.length - 1) {
      onAccept()
      // Reset for next time
      setClickCount(0)
      setButtonPosition({ x: 0, y: 0 })
      setImposterButtonPosition({ x: 0, y: 0 })
      setShowImposter(false)
    }
  }

  // Render the dialog content with a special button for "Here"
  const renderDialogContent = () => {
    if (clickCount === 7 && dialogContent === messages[7]) {
      return (
        <Typography variant="subtitle1">
          Click <TextButton onClick={handleHereClick}>Here</TextButton> to close
        </Typography>
      )
    }
    return (
      <Typography variant="subtitle1">
        {!!dialogContent ? dialogContent : messages[clickCount]}
      </Typography>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        style: { overflow: 'visible' }, // Allow content to overflow
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>{renderDialogContent()}</DialogContent>
      <DialogActions>
        {clickCount === buttonTexts.length ? (
          <Button onClick={onClose}>Cancel</Button>
        ) : clickCount > 0 ? (
          <Button
            onClick={(e) => {
              setClickCount(0)
              handleButtonClick(e)
            }}
          >
            Reset
          </Button>
        ) : null}
        <Box sx={{ position: 'relative', height: '40px', width: '100px' }}>
          {/* Real button */}
          <Button
            ref={buttonRef}
            variant="contained"
            color="primary"
            onClick={handleButtonClick}
            sx={{
              position: 'absolute',
              transform: `translate(${buttonPosition.x}px, ${buttonPosition.y}px)`,
              transition: 'transform 0.5s ease-out',
              whiteSpace: 'nowrap',
              zIndex: 1500, // Keep above other elements
            }}
          >
            {buttonTexts[clickCount]}
          </Button>

          {/* Imposter button */}
          {showImposter && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleImposterClick}
              sx={{
                position: 'absolute',
                transform: `translate(${imposterButtonPosition.x}px, ${imposterButtonPosition.y}px)`,
                transition: 'transform 0.5s ease-out',
                whiteSpace: 'nowrap',
                zIndex: 1500,
              }}
            >
              {buttonTexts[clickCount]}?
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  )
}
